import React, { useState, useEffect } from 'react';
import {
  FolderArchive,
  Plus,
  Search,
  FileText,
  File,
  Download,
  Building2,
  Calendar,
  User,
  Filter,
  MessageSquare,
  Share2,
  CheckCircle,
} from 'lucide-react';
import { DocumentItem } from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';

interface DocumentsPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const DocumentsPage: React.FC<DocumentsPageProps> = () => {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);
  const [downloadToast, setDownloadToast] = useState<string | null>(null);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  const [form, setForm] = useState({
    title: '',
    category: 'PO',
    projectId: '',
    fileType: 'PDF',
    fileSize: '1.4 MB',
  });

  const categories = [
    'PO',
    'BOQ',
    'Quotation',
    'Site Survey',
    'Completion Certificate',
    'Invoice',
    'Drawings',
  ];

  const fetchDocs = async () => {
    try {
      setLoading(true);
      const [dRes, pRes] = await Promise.all([
        fetch('/api/documents'),
        fetch('/api/projects'),
      ]);
      if (dRes.ok) {
        const dData = await dRes.json();
        if (Array.isArray(dData)) setDocuments(dData);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) {
            setForm((prev) => ({ ...prev, projectId: String(pData[0].id) }));
          }
        }
      }
    } catch (err) {
      console.warn('Documents fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchDocs();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = (Array.isArray(documents) ? documents : []).filter((d) => {
    return categoryFilter === 'ALL' || d.category === categoryFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Document Management Center
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900">
              Project Vault
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Organized project files: UBL PO copies, signed BOQs, survey photos, completion certs, and drawings.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => {
              if (documents.length > 0) {
                const first = documents[0];
                setShareData({
                  title: first.title,
                  docType: 'DOCUMENT',
                  projectName: `Project ${first.projectNumber}`,
                  referenceNumber: first.projectNumber,
                  fileUrl: first.fileUrl,
                  detailsSummary: `${first.category} file (${first.fileType}, ${first.fileSize})`,
                });
                setWhatsAppModalOpen(true);
              }
            }}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#075e54] border border-[#25D366]/40 rounded-lg text-xs font-bold transition-colors"
          >
            <MessageSquare className="w-4 h-4 text-[#25D366]" />
            <span>WhatsApp Dispatch</span>
          </button>

          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <Plus className="w-4 h-4 text-amber-400" />
            <span>Upload Document</span>
          </button>
        </div>
      </div>

      {/* Download Toast Notification */}
      {downloadToast && (
        <div className="fixed bottom-5 right-5 z-50 bg-[#0f2942] text-white px-4 py-2.5 rounded-xl shadow-xl border border-slate-700 flex items-center gap-2.5 text-xs animate-in slide-in-from-bottom-3 duration-200">
          <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{downloadToast}</span>
        </div>
      )}

      {/* Categories Bar */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 bg-white p-2.5 rounded-xl border border-slate-200">
        <button
          onClick={() => setCategoryFilter('ALL')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
            categoryFilter === 'ALL' ? 'bg-[#0f2942] text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          All Files
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              categoryFilter === c ? 'bg-[#0f2942] text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((doc) => (
          <div key={doc.id} className="bg-white rounded-xl border border-slate-200 shadow-2xs p-4 flex flex-col justify-between space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-xs text-slate-900 line-clamp-1">{doc.title}</h3>
                  <span className="text-[10px] font-semibold px-1.5 py-0.2 bg-slate-100 rounded text-slate-600 mt-1 inline-block">
                    {doc.category}
                  </span>
                </div>
              </div>

              <span className="text-[10px] font-mono text-slate-400 shrink-0">{doc.fileType}</span>
            </div>

            <div className="pt-2 border-t border-slate-100 text-xs text-slate-500 space-y-1">
              <div className="text-[11px] text-slate-600 truncate">Project: {doc.projectNumber}</div>
              <div className="flex items-center justify-between text-[10px] text-slate-400">
                <span>By: {doc.uploadedBy}</span>
                <span>{doc.fileSize}</span>
              </div>
            </div>

            <div className="pt-2 flex items-center justify-between gap-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setShareData({
                    title: doc.title,
                    docType: (doc.category === 'PO' ? 'PURCHASE_ORDER' : doc.category === 'BOQ' ? 'BOQ' : doc.category === 'Quotation' ? 'QUOTATION' : doc.category === 'Completion Certificate' ? 'COMPLETION_CERT' : doc.category === 'Site Survey' ? 'SITE_SURVEY' : doc.category === 'Invoice' ? 'INVOICE' : 'DOCUMENT') as any,
                    projectName: `Project ${doc.projectNumber}`,
                    referenceNumber: doc.projectNumber,
                    fileUrl: doc.fileUrl,
                    detailsSummary: `${doc.category} file (${doc.fileType}, ${doc.fileSize}) uploaded by ${doc.uploadedBy}`,
                  });
                  setWhatsAppModalOpen(true);
                }}
                className="text-xs font-bold text-[#075e54] hover:text-white flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366] px-2.5 py-1.5 rounded-lg transition-colors"
                title="Send document via WhatsApp"
              >
                <MessageSquare className="w-3.5 h-3.5 text-[#25D366] group-hover:text-white" />
                <span>WhatsApp</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setDownloadToast(`Downloading "${doc.title}" (${doc.fileType}, ${doc.fileSize})...`);
                  setTimeout(() => setDownloadToast(null), 3500);
                }}
                className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Document Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-md w-full p-5 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Attach Document to Project</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Target Project *</label>
                <select
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.projectId}
                  onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                >
                  {(Array.isArray(projects) ? projects : []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectNumber} - {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Document Category</label>
                <select
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">File Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. UBL PO # 7841 Signed Copy"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Upload File
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Share Modal */}
      <WhatsAppShareModal
        isOpen={whatsAppModalOpen}
        onClose={() => setWhatsAppModalOpen(false)}
        data={shareData}
      />
    </div>
  );
};
